import type { ReactNode } from 'react';
import { humanizeLabel, cardInfo, type Rec } from '@/components/chat/ChatPills';
import { MicIcon, WaveformIcon, SpeakerIcon } from '@/components/chat/ChatIcons';
import { CaptionBox } from '@/components/chat/CaptionBox';
import { AudioMoreMenu, type AudioMoreMenuProps } from '@/pages/chat/components/AudioMoreMenu';
import { InteractiveTextInput } from '@/pages/chat/components/InteractiveTextInput';
import { HistoryMenu, type CardsMessage, type RecentEntry } from '@/components/chat/UXView';
import ComposeCard, { type DraftCardMessage, type DraftSnapshot } from '@/components/Communications/ComposeCard';
import { NotePanel, type NoteCardMessage } from '@/components/chat/NotePanel';
import type { CommunicationOut } from '@/redux/internal-services/workspaceApiExtensions';
import type { Note } from '@/redux/internal-services/notesApi';
import { VoiceConnecting } from '@/pages/chat/components/VoiceConnecting';

// Control height shared by Speaker / Mic / End / ⋮ so the bar reads as one even row.
const CTRL_H = 'h-12';

// Mic/speaker buttons: full-width (split a row), same height as End, and double as live status —
// highlight when listening / speaking, red when muted.
const pill = (active: boolean, muted = false) =>
  `flex-1 flex items-center justify-center gap-2 ${CTRL_H} rounded-2xl text-sm font-medium border transition ${
    muted
      ? 'bg-danger/15 border-danger/30 text-danger-text hover:bg-danger/25'
      : active
        ? 'bg-ui-subtle border-ui-border text-ui-text'
        : 'bg-transparent border-ui-border text-ui-text2 hover:text-ui-text hover:border-ui-border3'
  }`;

export const HandsFreeView = ({
  confirm, cards, draft, note, index, setIndex, banner, recording, speaking, connecting = false, captionsOn, onExit,
  micMuted = false, onToggleMic, speakerMuted = false, onToggleSpeaker, moreMenu,
  history = [], onReplay, historyHasMore, onHistoryLoadMore, thread, hasCards,
  onDraftEdit, onDiscardDraft, onSentDraft, onSendText,
  onNoteClose, onNoteCaptureStart, onNoteCaptureStop, onNoteActive, onOpenTask, noteStopWord,
}: {
  /** A tool confirmation card (rendered by the caller) — takes the page over everything else, the
   *  live tool call is waiting on it. */
  confirm?: ReactNode;
  cards: CardsMessage | null;
  /** A SAM communication draft is the current surface — render it as the single hands-free page
   *  (editable; no pager) so the user can review/edit/send it by voice. */
  draft?: DraftCardMessage | null;
  /** A note-capture card is the current surface — render the NotePanel (voice-first note taking)
   *  as the single hands-free page. */
  note?: NoteCardMessage | null;
  index: number;
  setIndex: (n: number) => void;
  banner: string;
  recording: boolean;
  /** SAM's voice is actually sounding right now (usePCMAudioStream's `playing`, already ANDed with
   *  !speakerMuted by the caller) — drives the Speaker pill highlight and the caption's equalizer. */
  speaking: boolean;
  /** Session still connecting: show "Connecting to SAM" + Cancel instead of the live controls. */
  connecting?: boolean;
  captionsOn: boolean;
  onExit: () => void;
  micMuted?: boolean;
  onToggleMic?: () => void;
  speakerMuted?: boolean;
  onToggleSpeaker?: () => void;
  /** Captions toggle + mic/speaker device picker (⋮ near End). */
  moreMenu?: AudioMoreMenuProps;
  /** This session's card views — surfaced via the history (⋯) menu beside the pager arrows. */
  history?: RecentEntry[];
  onReplay?: (e: RecentEntry) => void;
  historyHasMore?: boolean;
  onHistoryLoadMore?: () => void;
  /** The chat thread, shown (as bubbles) for conversational turns when there are no cards. */
  thread: ReactNode;
  /** Whether SAM has listed cards — switches to the paginated single-card + caption view. */
  hasCards: boolean;
  onDraftEdit?: (snapshot: DraftSnapshot | null) => void;
  onDiscardDraft?: () => void;
  onSentDraft?: (comm: CommunicationOut) => void;
  /** When provided (chat_settings.interactive_text_input_enabled), shows a text input above the
   *  controls so the user can type to the live realtime agent alongside voice. */
  onSendText?: (text: string) => void;
  /** Note-capture card callbacks (mirror NotePanel props) — used when `note` is the surface. */
  onNoteClose?: () => void;
  onNoteCaptureStart?: () => void;
  onNoteCaptureStop?: (n?: Note | null) => void;
  onNoteActive?: (n: Note | null) => void;
  onOpenTask?: (taskId: string) => void;
  /** Spoken stop word for voice note dictation (chat_settings.note_stop_word); undefined = off. */
  noteStopWord?: string;
}) => {
  const records = cards?.records ?? [];
  const total = records.length;
  const pos = total > 0 ? Math.min(index, total - 1) : 0;
  const rec = total > 0 ? records[pos] as Rec : null;
  const info = rec && cards ? cardInfo(cards.module, rec) : null;
  const listening = recording && !micMuted;
  const draftLabel = draft?.draft?.channel
    ? ({ email: 'Email', sms: 'SMS', in_app: 'In-app' } as const)[draft.draft.channel]
    : 'Message';

  return (
    <div className="flex-1 flex flex-col min-h-0 px-4">
      {/* header: card counter + pager arrows (card view) or a Draft label (draft view), plus the
          session history (⋯) so prior views AND drafts (mails etc.) can be re-opened */}
      {!confirm && (hasCards || draft) && (
        <div className="flex-shrink-0 flex items-center justify-between py-3">
          {hasCards
            ? <span className="text-h2 text-ui-text">{total > 0 ? `${pos + 1}/${total}` : '0/0'}</span>
            : <span className="text-sm font-semibold uppercase tracking-wide text-ui-text2">{draftLabel}</span>}
          <div className="flex items-center gap-2">
            {hasCards && (
              <>
                <button onClick={() => setIndex(Math.max(0, index - 1))} disabled={pos <= 0}
                  className="w-9 h-9 rounded-full border border-ui-border text-ui-text text-lg flex items-center justify-center disabled:opacity-30 hover:bg-ui-hover transition">‹</button>
                <button onClick={() => setIndex(Math.min(total - 1, index + 1))} disabled={pos >= total - 1}
                  className="w-9 h-9 rounded-full border border-ui-border text-ui-text text-lg flex items-center justify-center disabled:opacity-30 hover:bg-ui-hover transition">›</button>
              </>
            )}
            {/* Previous card views + drafts this session — click one to re-open it. */}
            <HistoryMenu history={history} onReplay={onReplay} hasMore={historyHasMore} onLoadMore={onHistoryLoadMore} />
          </div>
        </div>
      )}

      {/* note: the voice-first capture/review card · draft: the editable compose card · card view:
          single large card · else: chat bubbles */}
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar py-3">
        {confirm ? (
          confirm
        ) : note ? (
          <NotePanel
            note={note}
            messageStrip={banner}
            history={history}
            onReplay={onReplay}
            onClose={onNoteClose ?? (() => undefined)}
            onCaptureStart={onNoteCaptureStart}
            onCaptureStop={onNoteCaptureStop}
            onActiveNote={onNoteActive}
            onOpenTask={onOpenTask}
            stopWord={noteStopWord}
          />
        ) : draft ? (
          <ComposeCard
            key={draft.communication_id ?? draft.id}
            communicationId={draft.communication_id}
            draft={draft.draft}
            seedRev={draft.rev}
            onDraftEdit={onDraftEdit}
            onDiscard={onDiscardDraft}
            onSent={onSentDraft}
          />
        ) : hasCards ? (
          rec && info ? (
            <div className="w-full rounded-3xl bg-ui-card border border-ui-border p-6">
              <div className="w-12 h-12 rounded-2xl bg-accent/20 flex items-center justify-center mb-5 text-2xl">📋</div>
              <p className="text-h3 text-ui-text leading-snug mb-4">{info.title}</p>
              {info.priority && (
                <span className="inline-flex items-center gap-1.5 text-warning-text text-sm font-medium">{humanizeLabel(info.priority)} ⚠️</span>
              )}
            </div>
          ) : (
            <p className="text-ui-text2 text-sm text-center mt-10">No items yet — ask SAM to list something.</p>
          )
        ) : (
          thread
        )}
      </div>

      {/* assistant caption — in card/draft view, when captions are on (toggled from the ⋮ menu) */}
      {(hasCards || draft || confirm) && <CaptionBox text={banner} show={captionsOn} tone="assistant" speaking={speaking} />}

      {/* controls: Speaker + Mic (full-width split) on top, then a red End filling the row beside the
          ⋮ menu. All four controls share one height (CTRL_H) so the bar lines up cleanly. */}
      <div className="flex-shrink-0 flex flex-col gap-2.5 pb-4 pt-1">
        {connecting ? (
          <VoiceConnecting onCancel={onExit} className="px-0 pb-0" />
        ) : (<>
        {onSendText && <InteractiveTextInput onSend={onSendText} wrapperClassName="" />}
        <div className="flex items-stretch gap-2.5">
          {onToggleSpeaker && <button onClick={onToggleSpeaker} title={speakerMuted ? 'Unmute speaker' : 'Mute speaker'} className={pill(speaking, speakerMuted)}>
            <SpeakerIcon className="h-5 w-5" /> {speakerMuted ? 'Speaker muted' : 'Speaker'}
          </button>}
          {onToggleMic && <button onClick={onToggleMic} title={micMuted ? 'Unmute microphone' : 'Mute microphone'} className={pill(listening, micMuted)}>
            <MicIcon className="h-5 w-5" /> {micMuted ? 'Mic muted' : 'Mic'}
          </button>}
        </div>
        <div className="flex items-stretch gap-2.5">
          <button onClick={onExit} className={`flex-1 ${CTRL_H} flex items-center justify-center gap-2 rounded-full bg-danger text-white text-base font-semibold shadow-[0_0_16px_rgba(239,68,68,0.4)] hover:brightness-110 transition`}>
            <WaveformIcon className="h-5 w-5" /> End
          </button>
          {moreMenu && (
            <AudioMoreMenu {...moreMenu} align="right" triggerClassName={`${CTRL_H} w-12 bg-ui-subtle border border-ui-border`} />
          )}
        </div>
        </>)}
      </div>
    </div>
  );
};

export default HandsFreeView;
